<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';
import CeaDynamicCatalogsAPI from 'dashboard/api/ceaDynamicCatalogs';
import LookupForm from './LookupForm.vue';
import DynamicCatalogEntryForm from './DynamicCatalogEntryForm.vue';
import CatalogWizard from './CatalogWizard.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

// Predefined catalogs. `feature` gates a tab behind an account feature flag
// (absent/null = always visible). All six serve service orders today.
const STATIC_TABS = [
  {
    key: 'order_types',
    i18nKey: 'CEA_LOOKUPS.TABS.ORDER_TYPES',
    feature: 'service_orders',
  },
  {
    key: 'order_motives',
    i18nKey: 'CEA_LOOKUPS.TABS.ORDER_MOTIVES',
    feature: 'service_orders',
  },
  {
    key: 'repair_codes',
    i18nKey: 'CEA_LOOKUPS.TABS.REPAIR_CODES',
    feature: 'service_orders',
  },
  {
    key: 'subcategory_tipos',
    i18nKey: 'CEA_LOOKUPS.TABS.SUBCATEGORY_TIPOS',
    feature: 'service_orders',
  },
  {
    key: 'exploitation_zones',
    i18nKey: 'CEA_LOOKUPS.TABS.EXPLOITATION_ZONES',
    feature: 'service_orders',
  },
  {
    key: 'cea_locations',
    i18nKey: 'CEA_LOOKUPS.TABS.OFICINAS',
    feature: 'service_orders',
  },
];

const DYNAMIC_TAB_PREFIX = 'dynamic:';

const activeTab = ref('');
const entries = ref([]);
const dynamicCatalogs = ref([]);
const loading = ref(true);
const showFormModal = ref(false);
const showCatalogWizard = ref(false);
const editingEntry = ref(null);

const isDynamicTab = computed(() =>
  activeTab.value.startsWith(DYNAMIC_TAB_PREFIX)
);

const activeDynamicCatalog = computed(() => {
  if (!isDynamicTab.value) return null;
  const catalogId = Number(activeTab.value.replace(DYNAMIC_TAB_PREFIX, ''));
  return (
    dynamicCatalogs.value.find(catalog => catalog.id === catalogId) || null
  );
});

const visibleStaticTabs = computed(() =>
  STATIC_TABS.filter(tab => !tab.feature || isCloudFeatureEnabled(tab.feature))
);

const allTabs = computed(() => [
  ...visibleStaticTabs.value.map(tab => ({ ...tab, isDynamic: false })),
  ...dynamicCatalogs.value.map(catalog => ({
    key: `${DYNAMIC_TAB_PREFIX}${catalog.id}`,
    label: catalog.name,
    isDynamic: true,
    catalog,
  })),
]);

// Keep activeTab pointing at a tab that actually exists; fall back to the
// first available one (or none when every catalog is hidden/empty).
const ensureActiveTab = () => {
  const tabs = allTabs.value;
  if (!tabs.some(tab => tab.key === activeTab.value)) {
    activeTab.value = tabs.length ? tabs[0].key : '';
  }
};

const isOficinasTab = computed(() => activeTab.value === 'cea_locations');

const fetchDynamicCatalogs = async () => {
  try {
    const response = await CeaDynamicCatalogsAPI.getCatalogs();
    dynamicCatalogs.value = response.data || [];
  } catch {
    useAlert(t('CEA_LOOKUPS.CATALOG_FETCH_ERROR'));
  }
};

const fetchEntries = async () => {
  if (!activeTab.value) {
    entries.value = [];
    loading.value = false;
    return;
  }

  try {
    loading.value = true;

    if (isDynamicTab.value && activeDynamicCatalog.value) {
      const response = await CeaDynamicCatalogsAPI.getEntries(
        activeDynamicCatalog.value.id
      );
      entries.value = response.data;
      return;
    }

    const response = await CeaLookupsAPI.get(activeTab.value);
    entries.value = response.data;
  } catch {
    useAlert(t('CEA_LOOKUPS.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const openNew = () => {
  if (!activeTab.value) return;
  editingEntry.value = null;
  showFormModal.value = true;
};

const openCatalogWizard = () => {
  showCatalogWizard.value = true;
};

const handleCatalogSaved = async createdCatalog => {
  showCatalogWizard.value = false;
  await fetchDynamicCatalogs();
  if (createdCatalog?.id) {
    activeTab.value = `${DYNAMIC_TAB_PREFIX}${createdCatalog.id}`;
  }
};

const openEdit = entry => {
  editingEntry.value = { ...entry };
  showFormModal.value = true;
};

const handleSaved = () => {
  showFormModal.value = false;
  editingEntry.value = null;
  fetchEntries();
};

const deleteEntry = async entry => {
  const name = entry.name || entry.description || entry.code;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CEA_LOOKUPS.DELETE_CONFIRM', { name }))) {
    return;
  }

  try {
    if (isDynamicTab.value && activeDynamicCatalog.value) {
      await CeaDynamicCatalogsAPI.deleteEntry(
        activeDynamicCatalog.value.id,
        entry.id
      );
    } else {
      await CeaLookupsAPI.delete(activeTab.value, entry.id);
    }
    useAlert(t('CEA_LOOKUPS.DELETE_SUCCESS'));
    fetchEntries();
  } catch {
    useAlert(t('CEA_LOOKUPS.DELETE_ERROR'));
  }
};

const formatHorario = horario => {
  if (!horario || typeof horario !== 'object') return '-';
  const parts = Object.entries(horario).map(
    ([day, hours]) => `${day}: ${hours}`
  );
  return parts.length ? parts.join(', ') : '-';
};

const formatServicios = servicios => {
  if (!Array.isArray(servicios) || !servicios.length) return '-';
  return servicios.join(', ');
};

const formatCustomValue = (field, value) => {
  if (field.field_type === 'boolean') {
    return value
      ? t('CEA_LOOKUPS.STATUS.ACTIVE')
      : t('CEA_LOOKUPS.STATUS.INACTIVE');
  }
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const customFieldsForActiveCatalog = computed(
  () => activeDynamicCatalog.value?.fields_schema || []
);

watch(activeTab, fetchEntries);

onMounted(async () => {
  await fetchDynamicCatalogs();
  ensureActiveTab();
  await fetchEntries();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('CEA_LOOKUPS.LOADING')"
    :no-records-found="!entries.length"
    :no-records-message="$t('CEA_LOOKUPS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CEA_LOOKUPS.TITLE')"
        :description="$t('CEA_LOOKUPS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-book-plus"
            :label="$t('CEA_LOOKUPS.NEW_CATALOG')"
            @click="openCatalogWizard"
          />
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('CEA_LOOKUPS.NEW')"
            :disabled="!activeTab"
            @click="openNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <div class="flex flex-wrap gap-1 p-1 rounded-lg bg-n-alpha-1 w-fit">
        <button
          v-for="tab in allTabs"
          :key="tab.key"
          type="button"
          class="px-3 py-1.5 text-sm rounded-md transition-colors"
          :class="
            activeTab === tab.key
              ? 'bg-n-solid-2 text-n-slate-12 font-medium shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = tab.key"
        >
          {{ tab.i18nKey ? $t(tab.i18nKey) : tab.label }}
        </button>
      </div>
    </template>

    <template #body>
      <!-- Oficinas: full-detail table -->
      <div
        v-if="isOficinasTab"
        class="overflow-x-auto rounded-lg border border-n-weak"
      >
        <table class="min-w-full divide-y divide-n-weak">
          <thead class="bg-n-solid-3">
            <tr>
              <th
                class="sticky left-0 z-10 bg-n-solid-3 px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.NAME') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.TIPO') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.SLUG') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.ADDRESS') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.COLONIA') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.MUNICIPIO') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.POSTAL_CODE') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.LATITUDE') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.LONGITUDE') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.PHONE') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.HOURS') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.SERVICES') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.STATUS') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.NOTES') }}
              </th>
              <th
                class="sticky right-0 z-10 bg-n-solid-3 px-4 py-3 text-right text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.OFICINAS.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak bg-n-solid-2">
            <tr
              v-for="entry in entries"
              :key="entry.id"
              class="hover:bg-n-alpha-1"
            >
              <td
                class="sticky left-0 z-10 bg-n-solid-2 px-4 py-3 text-sm font-medium text-n-slate-12 whitespace-nowrap"
              >
                {{ entry.name }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded"
                  :class="
                    entry.tipo === 'oficina'
                      ? 'bg-n-brand/10 text-n-blue-text'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  {{
                    entry.tipo === 'oficina'
                      ? $t('CEA_LOOKUPS.OFICINAS.TIPO_OFICINA')
                      : $t('CEA_LOOKUPS.OFICINAS.TIPO_CAJERO')
                  }}
                </span>
              </td>
              <td
                class="px-4 py-3 text-sm font-mono text-n-slate-11 whitespace-nowrap"
              >
                {{ entry.slug }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-12 whitespace-nowrap">
                {{ entry.address_street }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-12 whitespace-nowrap">
                {{ entry.colonia }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-12 whitespace-nowrap">
                {{ entry.municipio || '-' }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11 whitespace-nowrap">
                {{ entry.codigo_postal || '-' }}
              </td>
              <td
                class="px-4 py-3 text-sm font-mono text-n-slate-11 whitespace-nowrap"
              >
                {{ entry.latitude }}
              </td>
              <td
                class="px-4 py-3 text-sm font-mono text-n-slate-11 whitespace-nowrap"
              >
                {{ entry.longitude }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-12 whitespace-nowrap">
                {{ entry.telefono || '-' }}
              </td>
              <td
                class="px-4 py-3 text-sm text-n-slate-11 max-w-[200px] truncate"
              >
                {{ formatHorario(entry.horario) }}
              </td>
              <td
                class="px-4 py-3 text-sm text-n-slate-11 max-w-[200px] truncate"
              >
                {{ formatServicios(entry.servicios) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span
                  class="px-2 py-0.5 text-xs rounded"
                  :class="
                    entry.is_active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-solid-3 text-n-slate-10'
                  "
                >
                  {{
                    entry.is_active
                      ? $t('CEA_LOOKUPS.STATUS.ACTIVE')
                      : $t('CEA_LOOKUPS.STATUS.INACTIVE')
                  }}
                </span>
              </td>
              <td
                class="px-4 py-3 text-sm text-n-slate-11 max-w-[150px] truncate"
              >
                {{ entry.notas || '-' }}
              </td>
              <td
                class="sticky right-0 z-10 bg-n-solid-2 px-4 py-3 whitespace-nowrap"
              >
                <div class="flex items-center justify-end gap-2">
                  <Button
                    v-tooltip.top="$t('CEA_LOOKUPS.FORM.EDIT')"
                    icon="i-lucide-pen"
                    slate
                    xs
                    faded
                    @click="openEdit(entry)"
                  />
                  <Button
                    v-tooltip.top="$t('CEA_LOOKUPS.FORM.DELETE')"
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    @click="deleteEntry(entry)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Dynamic catalogs and standard tabs: table layout (columns = campos) -->
      <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="min-w-full divide-y divide-n-weak">
          <thead class="bg-n-solid-3">
            <tr>
              <th
                class="sticky left-0 z-10 bg-n-solid-3 px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.TABLE.CODE') }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.TABLE.NAME') }}
              </th>
              <th
                v-for="field in customFieldsForActiveCatalog"
                :key="`th-${field.key}`"
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ field.label }}
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.TABLE.STATUS') }}
              </th>
              <th
                class="sticky right-0 z-10 bg-n-solid-3 px-4 py-3 text-right text-xs font-medium uppercase text-n-slate-11"
              >
                {{ $t('CEA_LOOKUPS.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak bg-n-solid-2">
            <tr
              v-for="entry in entries"
              :key="entry.id"
              class="hover:bg-n-alpha-1"
            >
              <td
                class="sticky left-0 z-10 bg-n-solid-2 px-4 py-3 text-sm font-mono text-n-slate-11 whitespace-nowrap"
              >
                {{ entry.code }}
              </td>
              <td
                class="px-4 py-3 text-sm font-medium text-n-slate-12 whitespace-nowrap"
              >
                {{ entry.description }}
                <span
                  v-if="activeTab === 'order_types' && entry.order_class"
                  class="ml-2 px-2 py-0.5 text-xs font-medium rounded"
                  :class="
                    entry.order_class === 'service_order'
                      ? 'bg-n-teal-9/10 text-n-teal-11'
                      : 'bg-n-brand/10 text-n-blue-text'
                  "
                >
                  {{
                    entry.order_class === 'service_order'
                      ? 'Orden de Servicio'
                      : 'Orden de Trabajo'
                  }}
                </span>
              </td>
              <td
                v-for="field in customFieldsForActiveCatalog"
                :key="`${entry.id}-${field.key}`"
                class="px-4 py-3 text-sm text-n-slate-12 whitespace-nowrap"
              >
                {{ formatCustomValue(field, entry.custom_values?.[field.key]) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span
                  class="px-2 py-0.5 text-xs rounded"
                  :class="
                    entry.active !== false
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-solid-3 text-n-slate-10'
                  "
                >
                  {{
                    entry.active !== false
                      ? $t('CEA_LOOKUPS.STATUS.ACTIVE')
                      : $t('CEA_LOOKUPS.STATUS.INACTIVE')
                  }}
                </span>
              </td>
              <td
                class="sticky right-0 z-10 bg-n-solid-2 px-4 py-3 whitespace-nowrap"
              >
                <div class="flex items-center justify-end gap-2">
                  <Button
                    v-tooltip.top="$t('CEA_LOOKUPS.FORM.EDIT')"
                    icon="i-lucide-pen"
                    slate
                    xs
                    faded
                    @click="openEdit(entry)"
                  />
                  <Button
                    v-tooltip.top="$t('CEA_LOOKUPS.FORM.DELETE')"
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    @click="deleteEntry(entry)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <woot-modal
      v-model:show="showFormModal"
      :on-close="() => (showFormModal = false)"
    >
      <DynamicCatalogEntryForm
        v-if="isDynamicTab && activeDynamicCatalog"
        :catalog="activeDynamicCatalog"
        :entry="editingEntry"
        @saved="handleSaved"
        @cancel="showFormModal = false"
      />
      <LookupForm
        v-else
        :type="activeTab"
        :entry="editingEntry"
        @saved="handleSaved"
        @cancel="showFormModal = false"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showCatalogWizard"
      :on-close="() => (showCatalogWizard = false)"
    >
      <CatalogWizard
        @saved="handleCatalogSaved"
        @cancel="showCatalogWizard = false"
      />
    </woot-modal>
  </SettingsLayout>
</template>
